/**
 * Plan Editor Panel
 *
 * Context-aware editor: Project-level Master Plan when no artifact selected,
 * Artifact-level Plan when artifact selected
 */

import { useState } from 'react';
import Editor from '@monaco-editor/react';
import { useProjectStore, type ProjectArtifact } from '@/stores/projectStore';
import { useTheme } from '@/hooks/useTheme';
import { useToastStore } from '@/stores/toastStore';
import { buildUserHeaders } from '@/stores/userStore';
import { ArrowRight, ArrowLeft, Loader2, FileCode } from 'lucide-react';
import { transformApi, readStream, integrationApi } from '@/lib/api';
import { API_BASE_URL } from '@/lib/config';
import { ConfirmDialog } from '../ui/ConfirmDialog';

type ConfirmAction = 'toPrompt' | 'toLumaScript' | 'toLumaJson' | null;

type AttributeDataType = 'TEXT' | 'NUMBER' | 'BOOLEAN' | 'DATE' | 'JSON';
type AttributeScope = 'global' | 'local' | 'session';

interface AttributeMetadata {
  dataType: AttributeDataType;
  scope: AttributeScope;
  defaultValue?: string | number | boolean;
}

interface AttributeExtractionResult {
  metadata: AttributeMetadata;
  name?: string;
  description?: string;
  warnings: string[];
}

const DATA_TYPE_KEYWORDS: Array<{ keyword: string; type: AttributeDataType }> = [
  { keyword: 'text', type: 'TEXT' },
  { keyword: 'string', type: 'TEXT' },
  { keyword: 'number', type: 'NUMBER' },
  { keyword: 'numeric', type: 'NUMBER' },
  { keyword: 'integer', type: 'NUMBER' },
  { keyword: 'decimal', type: 'NUMBER' },
  { keyword: 'boolean', type: 'BOOLEAN' },
  { keyword: 'bool', type: 'BOOLEAN' },
  { keyword: 'yes/no', type: 'BOOLEAN' },
  { keyword: 'date', type: 'DATE' },
  { keyword: 'datetime', type: 'DATE' },
  { keyword: 'json', type: 'JSON' },
  { keyword: 'object', type: 'JSON' }
];

const SCOPE_KEYWORDS: Array<{ keyword: string; scope: AttributeScope }> = [
  { keyword: 'global', scope: 'global' },
  { keyword: 'organization', scope: 'global' },
  { keyword: 'session', scope: 'session' },
  { keyword: 'conversation', scope: 'session' },
  { keyword: 'local', scope: 'local' },
  { keyword: 'skill', scope: 'local' }
];

const BOOLEAN_TRUE = new Set(['true', 'yes', '1', 'enabled']);
const BOOLEAN_FALSE = new Set(['false', 'no', '0', 'disabled']);

function coerceDefaultValue(
  value: string,
  dataType: AttributeDataType,
  warnings: string[]
): string | number | boolean {
  const trimmed = value.trim();
  switch (dataType) {
    case 'NUMBER': {
      const numeric = parseFloat(trimmed);
      if (!Number.isFinite(numeric)) {
        warnings.push(`Default value "${value}" is not a valid number. Using 0.`);
        return 0;
      }
      return numeric;
    }
    case 'BOOLEAN': {
      const lower = trimmed.toLowerCase();
      if (BOOLEAN_TRUE.has(lower)) return true;
      if (BOOLEAN_FALSE.has(lower)) return false;
      warnings.push(`Default value "${value}" is not a recognizable boolean. Using false.`);
      return false;
    }
    case 'DATE':
    case 'TEXT':
    case 'JSON':
    default:
      return trimmed;
  }
}

function detectDataType(value: string): AttributeDataType | undefined {
  const lower = value.toLowerCase();
  for (const { keyword, type } of DATA_TYPE_KEYWORDS) {
    if (lower.includes(keyword)) {
      return type;
    }
  }
  return undefined;
}

function detectScope(value: string): AttributeScope | undefined {
  const lower = value.toLowerCase();
  for (const { keyword, scope } of SCOPE_KEYWORDS) {
    if (lower.includes(keyword)) {
      return scope;
    }
  }
  return undefined;
}

function extractAttributeDetailsFromPlan(
  plan: string,
  fallbackName: string,
  fallbackDescription?: string | null
): AttributeExtractionResult {
  const lines = plan
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  let dataType: AttributeDataType | undefined;
  let scope: AttributeScope | undefined;
  let defaultValueRaw: string | undefined;
  let name: string | undefined;
  let description: string | undefined;

  for (const rawLine of lines) {
    const line = rawLine.replace(/^[\-\*\u2022]+\s*/, '').trim();
    const normalizedLine = line.replace(/\*\*/g, '');

    const colonIndex = normalizedLine.indexOf(':');
    if (colonIndex !== -1) {
      const key = normalizedLine.slice(0, colonIndex).trim().toLowerCase();
      const value = normalizedLine.slice(colonIndex + 1).trim();

      if (!dataType && key.includes('data') && key.includes('type')) {
        dataType = detectDataType(value);
        continue;
      }

      if (!scope && key.includes('scope')) {
        scope = detectScope(value);
        continue;
      }

      if (!defaultValueRaw && key.includes('default')) {
        defaultValueRaw = value;
        continue;
      }

      if (!name && key.includes('name')) {
        name = value;
        continue;
      }

      if (!description && key.includes('description')) {
        description = value;
        continue;
      }
    }
  }

  if (!description) {
    const firstSentence = lines.find((line) => !/:/.test(line));
    if (firstSentence) {
      description = firstSentence;
    }
  }

  const warnings: string[] = [];
  if (!dataType) {
    warnings.push('Assuming data type TEXT; add "Data Type: ..." to the attribute plan to override.');
  }
  if (!scope) {
    warnings.push('Assuming global scope; add "Scope: ..." to the attribute plan to override.');
    scope = 'global';
  } else if (scope === 'session') {
    scope = 'global';
  }

  const metadata: AttributeMetadata = {
    dataType: dataType ?? 'TEXT',
    scope: (scope as AttributeScope | undefined) ?? 'global'
  };

  if (defaultValueRaw) {
    metadata.defaultValue = coerceDefaultValue(defaultValueRaw, metadata.dataType, warnings);
  }

  return {
    metadata,
    name: name || fallbackName,
    description: description || fallbackDescription || undefined,
    warnings
  };
}

export const PlanEditorPanel: React.FC = () => {
  const activeProjectId = useProjectStore((state) => state.activeProjectId);
  const selectedArtifactId = useProjectStore((state) => state.ui.selectedArtifactId);
  const projects = useProjectStore((state) => state.projects);
  const updateArtifact = useProjectStore((state) => state.updateArtifact);
  const loadArtifacts = useProjectStore((state) => state.loadArtifacts);
  const updateArtifactStage = useProjectStore((state) => state.updateArtifactStage);
  const updateProjectStage = useProjectStore((state) => state.updateProjectStage);
  const setActiveStage = useProjectStore((state) => state.setActiveStage);
  const { theme } = useTheme();
  const { addToast } = useToastStore();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [previousContent, setPreviousContent] = useState<string>('');
  const [transforming, setTransforming] = useState(false);

  // Get current project and artifact
  const project = activeProjectId ? projects[activeProjectId] : null;
  const artifact = project && selectedArtifactId ? project.artifacts[selectedArtifactId] : null;
  const artifactType = artifact?.type || null;
  const isAttributeArtifact = artifactType === 'ATTRIBUTE';
  const isIntegrationArtifact = artifactType === 'INTEGRATION';
  const isMetadataOnlyArtifact = isAttributeArtifact || isIntegrationArtifact;

  // Context detection
  const isProjectContext = !selectedArtifactId;

  // Get plan content based on context
  const artifactPlan = artifact?.stages.ARTIFACT_PLAN?.content || '';
  const metadataPlanFallback = isMetadataOnlyArtifact ? (artifact?.seedPlan || '') : '';
  const normalizedArtifactPlan = artifactPlan.trim() ? artifactPlan : metadataPlanFallback;
  const plan = isProjectContext
    ? project?.stages.PLAN?.content || ''
    : normalizedArtifactPlan;

  if (!project) {
    return (
      <div className="panel-container">
        <div className="panel-content flex items-center justify-center h-full text-muted-foreground">
          <p className="text-sm">No project selected. Create or open a project to get started.</p>
        </div>
      </div>
    );
  }

  const handleChange = (value: string | undefined) => {
    if (value !== undefined && activeProjectId) {
      if (isProjectContext) {
        // Update project-level PLAN stage
        updateProjectStage(activeProjectId, 'PLAN', value, true);
      } else if (selectedArtifactId) {
        // Update artifact-level ARTIFACT_PLAN stage
        updateArtifactStage(activeProjectId, selectedArtifactId, 'ARTIFACT_PLAN', value, true);
        if (isMetadataOnlyArtifact) {
          updateArtifact(activeProjectId, selectedArtifactId, { seedPlan: value });
        }
      }
    }
  };

  const handleToPromptClick = () => {
    // Only available in artifact context
    if (isProjectContext || !artifact) return;

    const promptStage = artifact.stages.PROMPT;
    if (promptStage && promptStage.content.trim()) {
      setConfirmAction('toPrompt');
    } else {
      executeToPrompt();
    }
  };

  const handleToLumaScriptClick = () => {
    const lumascriptContent = isProjectContext
      ? project?.stages.LUMASCRIPT?.content
      : artifact?.stages.LUMASCRIPT?.content;

    if (lumascriptContent && lumascriptContent.trim()) {
      setConfirmAction('toLumaScript');
    } else {
      executeToLumaScript();
    }
  };

  const executeToPrompt = async () => {
    // Only works in artifact context
    if (!plan || !activeProjectId || !selectedArtifactId || isProjectContext || !artifact) return;

    const promptStage = artifact.stages.PROMPT;
    setPreviousContent(promptStage?.content || '');
    setTransforming(true);

    try {
      const stream = await transformApi.planToPrompt(plan);
      let newPrompt = '';
      for await (const chunk of readStream(stream)) {
        newPrompt += chunk;
      }
      updateArtifactStage(activeProjectId, selectedArtifactId, 'PROMPT', newPrompt, false);
      setActiveStage('PROMPT');
      addToast('success', 'Successfully transformed Plan to Prompt');
    } catch (error: any) {
      console.error('Transform error:', error);
      addToast('error', `Failed to transform: ${error.message || 'Unknown error'}`);
      // Restore previous content on error
      updateArtifactStage(activeProjectId, selectedArtifactId, 'PROMPT', previousContent, false);
    } finally {
      setTransforming(false);
    }
  };

  const executeToLumaScript = async () => {
    if (!plan || !activeProjectId) return;

    setTransforming(true);

    try {
      if (isProjectContext) {
        // Project context: Transform master plan to project-level LumaScript
        const lumascriptStage = project?.stages.LUMASCRIPT;
        setPreviousContent(lumascriptStage?.content || '');

        // Use transformation API for project-level plan
        const stream = await transformApi.planToLumaScript({
          plan,
          nodeLibrary: []  // TODO: Pass actual node library when available
        });
        let lumaScriptJson = '';
        for await (const chunk of readStream(stream)) {
          lumaScriptJson += chunk;
        }

        // Strip markdown code fences if present
        lumaScriptJson = lumaScriptJson.replace(/^```json\s*\n?/gm, '').replace(/\n?```\s*$/gm, '').trim();

        // Validate JSON before storing
        JSON.parse(lumaScriptJson);
        await updateProjectStage(activeProjectId, 'LUMASCRIPT', lumaScriptJson, false);
        setActiveStage('LUMASCRIPT');
        addToast('success', 'Successfully transformed Agent Plan to LumaScript');
      } else if (selectedArtifactId && artifact) {
        // Artifact context: Transform artifact plan to artifact-level LumaScript
        const lumascriptStage = artifact.stages.LUMASCRIPT;
        setPreviousContent(lumascriptStage?.content || '');

        const response = await fetch(`${API_BASE_URL}/generation/script`, {
          method: 'POST',
          headers: buildUserHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            artifactId: selectedArtifactId
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to generate LumaScript: ${response.statusText}`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let lumaScriptJson = '';

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            lumaScriptJson += chunk;
          }
        }

        // Strip markdown code fences if present
        lumaScriptJson = lumaScriptJson.replace(/^```json\s*\n?/gm, '').replace(/\n?```\s*$/gm, '').trim();

        // Validate JSON before storing
        JSON.parse(lumaScriptJson);
        updateArtifactStage(activeProjectId, selectedArtifactId, 'LUMASCRIPT', lumaScriptJson, false);
        setActiveStage('LUMASCRIPT');
        addToast('success', 'Successfully transformed Artifact Plan to LumaScript');
      }
    } catch (error: any) {
      console.error('Transform error:', error);
      addToast('error', `Failed to transform: ${error.message || 'Unknown error'}`);
      // Restore previous content on error
      if (isProjectContext) {
        await updateProjectStage(activeProjectId, 'LUMASCRIPT', previousContent, false);
      } else if (selectedArtifactId) {
        updateArtifactStage(activeProjectId, selectedArtifactId, 'LUMASCRIPT', previousContent, false);
      }
    } finally {
      setTransforming(false);
    }
  };

  const handleToLumaJson = async () => {
    if (!artifact || !activeProjectId || !selectedArtifactId) {
      return;
    }

    if (!isMetadataOnlyArtifact) {
      setActiveStage('LUMAJSON');
      return;
    }

    if (!plan || !plan.trim()) {
      addToast('warning', 'Add plan details before generating LumaJSON.');
      setActiveStage('LUMAJSON');
      return;
    }

    try {
      if (isAttributeArtifact) {
        const extraction = extractAttributeDetailsFromPlan(plan, artifact.name, artifact.description);
        const updates: Partial<ProjectArtifact> = {
          lumaJson: extraction.metadata
        };

        if (extraction.name && extraction.name !== artifact.name) {
          updates.name = extraction.name;
        }

        if (
          extraction.description &&
          extraction.description !== (artifact.description || '').trim()
        ) {
          updates.description = extraction.description;
        }

        await updateArtifact(activeProjectId, selectedArtifactId, updates);
        await loadArtifacts(activeProjectId);

        if (extraction.warnings.length > 0) {
          extraction.warnings.forEach((warning) => addToast('warning', warning));
        }

        addToast('success', 'Generated attribute metadata from the plan.');
      } else if (isIntegrationArtifact) {
        const response = await integrationApi.generateFromPlan(activeProjectId, selectedArtifactId, plan);
        await loadArtifacts(activeProjectId);
        if (response.warnings?.length) {
          response.warnings.forEach((warning: string) => addToast('warning', warning));
        }
        addToast('success', 'Generated integration metadata from the plan.');
      }
    } catch (error: any) {
      console.error('[PlanEditorPanel] Failed to generate metadata:', error);
      const details = error?.response?.data?.details || error?.response?.data?.error;
      if (details) {
        addToast('error', `Failed to generate metadata: ${details}`);
      } else {
        addToast('error', error?.message || 'Failed to generate metadata from the plan.');
      }
    }

    setActiveStage('LUMAJSON');
  };

  const handleConfirm = () => {
    const action = confirmAction;
    setConfirmAction(null);
    switch (action) {
      case 'toPrompt':
        executeToPrompt();
        break;
      case 'toLumaScript':
        executeToLumaScript();
        break;
      case 'toLumaJson':
        void handleToLumaJson();
        break;
    }
  };

  const handleCancel = () => {
    setConfirmAction(null);
  };

  return (
    <div className="panel-container">
      {/* Conversion Buttons Bar */}
      <div className="h-8 border-b border-border bg-background flex items-center justify-center gap-2 px-4">
        {transforming ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Transforming...</span>
          </div>
        ) : (
          <>
            {/* "To Prompt" only available in artifact context */}
            {!isProjectContext && (
              <button
                onClick={handleToPromptClick}
                disabled={!plan || transforming}
                className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-orange-500/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                <ArrowLeft className="w-3 h-3" />
                To Prompt
              </button>
            )}
            {isMetadataOnlyArtifact ? (
              <button
                onClick={() => {
                  if (!plan || transforming) return;
                  if (!artifact?.lumaJson) {
                    void handleToLumaJson();
                  } else {
                    setConfirmAction('toLumaJson');
                  }
                }}
                disabled={!plan || transforming}
                className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-orange-500/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                To LumaJSON
                <FileCode className="w-3 h-3" />
              </button>
            ) : (
              <button
                onClick={handleToLumaScriptClick}
                disabled={!plan || transforming}
                className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-orange-500/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                To LumaScript
                <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </>
        )}
      </div>

      <div className="panel-content">
        <Editor
          key={theme}
          height="100%"
          language="markdown"
          value={plan}
          onChange={handleChange}
          theme={theme === 'dark' ? 'vs-dark' : 'vs-light'}
          options={{
            minimap: { enabled: false },
            lineNumbers: 'on',
            wordWrap: 'on',
            fontSize: 14,
            padding: { top: 16, bottom: 16 }
          }}
        />
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmAction !== null}
        title="Overwrite Existing Content?"
        message={
          confirmAction === 'toPrompt'
            ? 'This will overwrite the existing Prompt content. Are you sure?'
            : confirmAction === 'toLumaScript'
            ? 'This will overwrite the existing LumaScript content. Are you sure?'
            : 'This will overwrite the existing LumaJSON content. Are you sure?'
        }
        confirmLabel="Overwrite"
        cancelLabel="Cancel"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
};
